import React, { useState } from "react";
import "../styles/Volunteers.css";

import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
} from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/* ---------------------------
   Dropdown Options
---------------------------- */

const LOCATIONS = [
  "Kumarapalayam",
  "Pallipalayam",
  "Sankagiri",
  "Tiruchengode",
];

const CATEGORIES = [
  "Retail & Shopping",
  "Food & Beverage",
  "Health & Wellness",
  "Services & Repairs",
  "Education & Training",
  "Construction & Property",
  "Professional & Financial",
  "Transport & Travel",
  "Manufacturing & Wholesale",
  "Other / Miscellaneous",
];

export default function Volunteers() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [uploading, setUploading] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [checkingMobile, setCheckingMobile] = useState(false);
  const [sampleImage, setSampleImage] = useState("");

  const [shop, setShop] = useState({
    "Shop Name": "",
    "Mobile Number": "",
    Location: "",
    Category: "",
    "Shop Image": "",

    Description: "",
    OwnerName: "",
    OpeningTime: "",
    ClosingTime: "",

    Instagram: "",
    Facebook: "",
    X: "",
    Threads: "",
    Youtube: "",

    VolunteerSelfie: "",
    ShopFrontPhoto: "",
  });

  /* ---------------------------
     Change Handler
  ---------------------------- */

  const handleChange = (key, value) => {
    setShop((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /* ---------------------------
     ImgBB Upload
  ---------------------------- */

  const uploadToImgBB = async (file) => {
    const key = process.env.REACT_APP_IMGBB_KEY;

    if (!file || !key) return "";

    const fd = new FormData();
    fd.append("image", file);

    const res = await fetch(
      `https://api.imgbb.com/1/upload?key=${key}`,
      {
        method: "POST",
        body: fd,
      }
    );

    const data = await res.json();

    return data?.data?.url || "";
  };

  /* ---------------------------
     Upload Shop Image
  ---------------------------- */

  const uploadShopImage = async (file) => {
    if (!file) return;

    try {
      setUploading(true);

      const imageUrl = await uploadToImgBB(file);

      if (imageUrl) {
        handleChange("Shop Image", imageUrl);
      } else {
        alert("Image upload failed");
      }
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* ---------------------------
     Upload Extra Image
  ---------------------------- */

  const uploadExtraImage = async (file, fieldName) => {
    if (!file) return;

    try {
      setUploading(true);

      const imageUrl = await uploadToImgBB(file);

      if (imageUrl) {
        handleChange(fieldName, imageUrl);
      } else {
        alert("Image upload failed");
      }
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* ---------------------------
     Validate Mobile
  ---------------------------- */

  const validateMobile = async () => {
    const mobile = (shop["Mobile Number"] || "")
      .replace(/\D/g, "")
      .trim();

    if (!/^\d{10}$/.test(mobile)) {
      alert("Enter a valid 10 digit mobile number.");
      return;
    }

    try {
      setCheckingMobile(true);

      /* ---------------------------
         Check Existing Shops
      ---------------------------- */

      const shopSnap = await getDocs(
        collection(db, "shops")
      );

      const existsInShops = shopSnap.docs.some((doc) => {
        const existing = String(
          doc.data()["Mobile Number"] || ""
        )
          .replace(/\D/g, "")
          .trim();

        return existing === mobile;
      });

      if (existsInShops) {
        alert(
          "❌ This mobile number already exists in Shops."
        );

        setMobileVerified(false);
        return;
      }

      /* ---------------------------
         Check Existing Submissions
      ---------------------------- */

      const submissionSnap = await getDocs(
        collection(db, "businessSubmissions")
      );

      const existsInSubmissions =
        submissionSnap.docs.some((doc) => {
          const data = doc.data();

          const existing = String(
            data["Mobile Number"] || ""
          )
            .replace(/\D/g, "")
            .trim();

          return (
            existing === mobile &&
            data.status !== "REJECTED"
          );
        });

      if (existsInSubmissions) {
        alert(
          "❌ This business is already submitted or waiting for approval."
        );

        setMobileVerified(false);
        return;
      }

      alert("✅ Mobile number verified.");
      setMobileVerified(true);

    } catch (err) {
      console.error(err);
      alert("Validation failed.");
      setMobileVerified(false);
    } finally {
      setCheckingMobile(false);
    }
  };

  /* ---------------------------
     Submit Business
  ---------------------------- */

  const submitBusiness = async () => {
    if (!user?.uid) {
      alert("Please login first.");
      return;
    }

    const mobile = shop["Mobile Number"] || "";

    /* ---------------------------
       Basic Validation
    ---------------------------- */

    if (!shop["Shop Name"].trim()) {
      alert("Please enter Shop Name.");
      return;
    }

    if (!/^\d{10}$/.test(mobile)) {
      alert(
        "Mobile number must be exactly 10 digits."
      );
      return;
    }

    if (!shop.Location) {
      alert("Please select a location.");
      return;
    }

    if (!shop.Category) {
      alert("Please select a category.");
      return;
    }

    if (!shop["Shop Image"]) {
      alert("Please upload a shop image.");
      return;
    }

    if (!shop.VolunteerSelfie) {
      alert("Please upload volunteer selfie.");
      return;
    }

    if (!shop.ShopFrontPhoto) {
      alert("Please upload shop front photo.");
      return;
    }

    if (!mobileVerified) {
      alert(
        "Please validate the mobile number first."
      );
      return;
    }

    /* ---------------------------
       Normalize Mobile
    ---------------------------- */

    const normalizedMobile = mobile
      .replace(/\D/g, "")
      .trim();

    try {
      setUploading(true);

      /* ---------------------------
         Final Duplicate Check
      ---------------------------- */

      const shopSnap = await getDocs(
        collection(db, "shops")
      );

      const existsInShops = shopSnap.docs.some((doc) => {
        const existing = String(
          doc.data()["Mobile Number"] || ""
        )
          .replace(/\D/g, "")
          .trim();

        return existing === normalizedMobile;
      });

      if (existsInShops) {
        alert(
          "This mobile number is already registered as a shop."
        );

        setUploading(false);
        return;
      }

      /* ---------------------------
         Final Submission Check
      ---------------------------- */

      const submissionSnap = await getDocs(
        collection(db, "businessSubmissions")
      );

      const existsInSubmissions =
        submissionSnap.docs.some((doc) => {
          const data = doc.data();

          const existing = String(
            data["Mobile Number"] || ""
          )
            .replace(/\D/g, "")
            .trim();

          return (
            existing === normalizedMobile &&
            data.status !== "REJECTED"
          );
        });

      if (existsInSubmissions) {
        alert(
          "This business has already been submitted and is waiting for review."
        );

        setUploading(false);
        return;
      }

      /* ---------------------------
         Create Pending Submission
      ---------------------------- */

      const docRef = await addDoc(
        collection(db, "businessSubmissions"),
        {
          /* Business Information */

          "Shop Name": shop["Shop Name"].trim(),

          "Mobile Number": normalizedMobile,

          Location: shop.Location,

          Category: shop.Category,

          "Shop Image": shop["Shop Image"],

          Description:
            shop.Description || "",

          OwnerName:
            shop.OwnerName || "",

          OpeningTime:
            shop.OpeningTime || "",

          ClosingTime:
            shop.ClosingTime || "",

          /* Social Media */

          Instagram:
            shop.Instagram || "",

          Facebook:
            shop.Facebook || "",

          X:
            shop.X || "",

          Threads:
            shop.Threads || "",

          Youtube:
            shop.Youtube || "",

          /* Verification */

          VolunteerSelfie:
            shop.VolunteerSelfie,

          ShopFrontPhoto:
            shop.ShopFrontPhoto,

          /* Shop Defaults */

          Likes: 0,

          Shares: 0,

          LikedUsers: [],

          "Type (Premium/Normal)": "Normal",

          uid: "",

          /* Volunteer Information */

          addedByVolunteer: true,

          volunteerUid:
            user.uid,

          volunteerName:
            user.displayName ||
            "Community Volunteer",

          volunteerEmail:
            user.email || "",

          /* Review Status */

          status: "PENDING_REVIEW",

          approved: false,

          rejected: false,

          /* Payment */

          rewardAmount: 10,

          rewardPaid: false,

          /* Dates */

          createdAt:
            serverTimestamp(),
        }
      );

      /* ---------------------------
         IMPORTANT:
         NO CREDIT HERE
      ---------------------------- */

      console.log(
        "Volunteer business submission created:",
        docRef.id
      );

      alert(
        "✅ Business submitted successfully!\n\n" +
        "Your submission is now waiting for Admin approval.\n\n" +
        "₹10 will be credited after the business is approved."
      );

      /* ---------------------------
         Reset Form
      ---------------------------- */

      setShop({
        "Shop Name": "",
        "Mobile Number": "",
        Location: "",
        Category: "",
        "Shop Image": "",

        Description: "",
        OwnerName: "",
        OpeningTime: "",
        ClosingTime: "",

        Instagram: "",
        Facebook: "",
        X: "",
        Threads: "",
        Youtube: "",

        VolunteerSelfie: "",
        ShopFrontPhoto: "",
      });

      setMobileVerified(false);

      navigate("/volunteers");

    } catch (err) {
      console.error(
        "Failed to submit business:",
        err
      );

      alert(
        "Failed to submit business. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="shops-wrap">

      <div className="shops-container">

        {/* Header */}

        <div className="premium-banner">
          🤝 Community Volunteer - Submit Local Businesses & earn Rs.10 for each business listing.
        </div>

        <h2>Add Business</h2>

        {/* Shop Image */}

        <div className="shop-image-upload">

          {shop["Shop Image"] ? (
            <img
              src={shop["Shop Image"]}
              alt="Shop"
              className="shop-preview"
            />
          ) : (
            <div className="shop-placeholder">
              🏪
            </div>
          )}

          <label className="upload-btn">

            Upload Shop Image

            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) =>
                uploadShopImage(
                  e.target.files[0]
                )
              }
            />

          </label>

          <div
            className="sample-photo-link"
            onClick={() =>
              setSampleImage(
                "https://pbs.twimg.com/media/HN-f3RJaMAE3VIm?format=jpg&name=large"
              )
            }
          >
            📷 View Sample Photo
          </div>

          {uploading && (
            <p style={{ marginTop: 8 }}>
              Uploading...
            </p>
          )}

        </div>

        {/* Shop Name */}

        <input
          className="shop-search"
          placeholder="Business Name"
          value={shop["Shop Name"] || ""}
          onChange={(e) =>
            handleChange(
              "Shop Name",
              e.target.value
            )
          }
        />

        {/* Mobile */}

        <input
          className="shop-search"
          placeholder="Business Mobile Number"
          maxLength={10}
          value={shop["Mobile Number"] || ""}
          onChange={(e) => {

            handleChange(
              "Mobile Number",
              e.target.value.replace(
                /\D/g,
                ""
              )
            );

            setMobileVerified(false);

          }}
        />

        <div
          style={{
            marginTop: 10,
            marginBottom: 12,
          }}
        >

          <button
            type="button"
            className="validate-btn"
            onClick={validateMobile}
            disabled={checkingMobile}
          >

            {checkingMobile
              ? "Checking..."
              : mobileVerified
              ? "✅ Mobile Verified"
              : "Validate Mobile"}

          </button>

        </div>

        {/* Location */}

        <select
          className="shop-select"
          value={shop.Location || ""}
          onChange={(e) =>
            handleChange(
              "Location",
              e.target.value
            )
          }
        >

          <option value="">
            Select Location
          </option>

          {LOCATIONS.map((l) => (
            <option
              key={l}
              value={l}
            >
              {l}
            </option>
          ))}

        </select>

        {/* Category */}

        <select
          className="shop-select"
          value={shop.Category || ""}
          onChange={(e) =>
            handleChange(
              "Category",
              e.target.value
            )
          }
        >

          <option value="">
            Select Category
          </option>

          {CATEGORIES.map((c) => (
            <option
              key={c}
              value={c}
            >
              {c}
            </option>
          ))}

        </select>

        {/* Description */}

        <textarea
          className="shop-search"
          rows={4}
          placeholder="Business Description"
          value={shop.Description || ""}
          onChange={(e) =>
            handleChange(
              "Description",
              e.target.value
            )
          }
        />

        {/* Owner */}

        <input
          className="shop-search"
          placeholder="Owner Name (Optional)"
          value={shop.OwnerName || ""}
          onChange={(e) =>
            handleChange(
              "OwnerName",
              e.target.value
            )
          }
        />

        {/* Business Hours */}

        <div className="time-row">

          <div className="time-box">

            <label>
              Opens
            </label>

            <input
              type="time"
              value={
                shop.OpeningTime || ""
              }
              onChange={(e) =>
                handleChange(
                  "OpeningTime",
                  e.target.value
                )
              }
            />

          </div>

          <div className="time-box">

            <label>
              Closes
            </label>

            <input
              type="time"
              value={
                shop.ClosingTime || ""
              }
              onChange={(e) =>
                handleChange(
                  "ClosingTime",
                  e.target.value
                )
              }
            />

          </div>

        </div>

        {/* Social Links */}

        <input
          className="shop-search"
          placeholder="Instagram URL"
          value={shop.Instagram || ""}
          onChange={(e) =>
            handleChange(
              "Instagram",
              e.target.value
            )
          }
        />

        <input
          className="shop-search"
          placeholder="Facebook URL"
          value={shop.Facebook || ""}
          onChange={(e) =>
            handleChange(
              "Facebook",
              e.target.value
            )
          }
        />

        <input
          className="shop-search"
          placeholder="X (Twitter) URL"
          value={shop.X || ""}
          onChange={(e) =>
            handleChange(
              "X",
              e.target.value
            )
          }
        />

        <input
          className="shop-search"
          placeholder="Threads URL"
          value={shop.Threads || ""}
          onChange={(e) =>
            handleChange(
              "Threads",
              e.target.value
            )
          }
        />

        <input
          className="shop-search"
          placeholder="YouTube URL"
          value={shop.Youtube || ""}
          onChange={(e) =>
            handleChange(
              "Youtube",
              e.target.value
            )
          }
        />

        {/* Verification */}

        <h3 style={{ marginTop: 20 }}>
          Verification Photos
        </h3>

        <div className="verification-upload-grid">

          {/* Volunteer Selfie */}

          <div className="verification-box">

            {shop.VolunteerSelfie ? (
              <img
                src={
                  shop.VolunteerSelfie
                }
                alt=""
                className="verification-preview"
              />
            ) : (
              <div className="verification-placeholder">
                🤳
              </div>
            )}

            <label className="upload-btn">

              Upload Selfie

              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) =>
                  uploadExtraImage(
                    e.target.files[0],
                    "VolunteerSelfie"
                  )
                }
              />

            </label>

            <div
              className="sample-photo-link"
              onClick={() =>
                setSampleImage(
                  "https://pbs.twimg.com/media/HN-f3RBbIAAJe7m?format=jpg&name=large"
                )
              }
            >
              📷 Sample Selfie
            </div>

          </div>

          {/* Shop Front */}

          <div className="verification-box">

            {shop.ShopFrontPhoto ? (
              <img
                src={
                  shop.ShopFrontPhoto
                }
                alt=""
                className="verification-preview"
              />
            ) : (
              <div className="verification-placeholder">
                🏪
              </div>
            )}

            <label className="upload-btn">

              Upload with staff

              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) =>
                  uploadExtraImage(
                    e.target.files[0],
                    "ShopFrontPhoto"
                  )
                }
              />

            </label>

            <div
              className="sample-photo-link"
              onClick={() =>
                setSampleImage(
                  "https://pbs.twimg.com/media/HN-f3QxaEAAHEwS?format=jpg&name=large"
                )
              }
            >
              📷 Sample photo
            </div>

          </div>

        </div>

        {/* Declaration */}

        <div className="declaration">

          <input
            type="checkbox"
            required
          />

          <span>
            I confirm that this business exists
            and the submitted information is
            accurate, no duplicate or existing
            business is added. If it is wrong the
            credited amount in wallet will not be
            processed.
          </span>

        </div>

        {/* Submit */}

        <button
          className="pf-logout"
          onClick={submitBusiness}
          disabled={uploading}
        >

          {uploading
            ? "Submitting..."
            : "Submit Business"}

        </button>

      </div>

      {/* Sample Image Modal */}

      {sampleImage && (
        <div
          className="sample-modal"
          onClick={() =>
            setSampleImage("")
          }
        >

          <div
            className="sample-modal-content"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <button
              className="sample-close"
              onClick={() =>
                setSampleImage("")
              }
            >
              ✕
            </button>

            <img
              src={sampleImage}
              alt="Sample"
            />

          </div>

        </div>
      )}

    </div>
  );
}